import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const EXCEL_COLS = [
  { col: 'Category',    note: 'Category name — leave blank to inherit from row above', required: false },
  { col: 'Item Name ✱', note: 'Item / product name — required', required: true },
  { col: 'Base Price',  note: 'Single price. Leave blank if using size variants.', required: false },
  { col: 'Size 1',      note: 'First size/variant label (e.g. Small, Large)', required: false },
  { col: 'Price 1',     note: 'Price for Size 1', required: false },
  { col: 'Size 2–10',   note: 'Add more size/price pairs up to Size 10 & Price 10', required: false },
];

const MODES = [
  { id: 'ai',       icon: '🤖', label: 'AI Scan',     sub: 'Photo / PDF',    badge: '~$0.01/file', color: '#2196F3', bg: '#E3F2FD', light: '#1565C0' },
  { id: 'excel',    icon: '📋', label: 'Our Template', sub: 'Excel .xlsx',    badge: 'Free ✓',      color: '#43A047', bg: '#E8F5E9', light: '#1B5E20' },
  { id: 'excel-ai', icon: '🧠', label: 'Smart Excel',  sub: 'Any format',     badge: 'AI powered',  color: '#7B1FA2', bg: '#F3E5F5', light: '#4A148C' },
];

// Detect primary language from parsed menu result
function detectLang(menu) {
  if (!menu?.categories) return null;
  const items = menu.categories.flatMap(c => c.items || []);
  const hasEn = items.some(i => i.name?.trim());
  const hasAr = items.some(i => i.name_ar?.trim());
  if (hasEn && hasAr) return 'bilingual';
  if (hasAr) return 'arabic';
  return 'english';
}

export default function ImportMenu() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('ai');
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRest, setSelectedRest] = useState('');
  const [appendMode, setAppendMode] = useState(false);
  const [creating, setCreating] = useState(false);
  const [balance, setBalance] = useState(null);
  const [expandedCat, setExpandedCat] = useState(null);
  const [showCols, setShowCols] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [balanceInput, setBalanceInput] = useState('');
  const [savingBalance, setSavingBalance] = useState(false);
  const [showBalanceMgr, setShowBalanceMgr] = useState(false);
  const fileInputRef = useRef();

  const refreshBalance = () => api.get('/import/balance').then(r => setBalance(r.data)).catch(() => {});

  useEffect(() => {
    refreshBalance();
    api.get('/brands').then(r => setRestaurants(r.data)).catch(() => {});
  }, []);

  const handleSaveBalance = async () => {
    const amount = parseFloat(balanceInput);
    if (isNaN(amount) || amount < 0) return toast.error('Enter a valid amount');
    setSavingBalance(true);
    try {
      const res = await api.post('/import/balance', { amount });
      setBalance(prev => ({ ...prev, ...res.data }));
      setBalanceInput('');
      setShowBalanceMgr(false);
      toast.success(`Balance set to $${amount.toFixed(2)}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save balance');
    } finally { setSavingBalance(false); }
  };

  // Object URLs for image previews — revoke on change
  useEffect(() => {
    const urls = {};
    files.forEach(f => {
      if (f.type.startsWith('image/')) urls[f.name] = URL.createObjectURL(f);
    });
    setPreviews(urls);
    return () => Object.values(urls).forEach(u => URL.revokeObjectURL(u));
  }, [files]);

  const switchMode = (m) => {
    if (m === mode) return;
    setMode(m); setFiles([]); setStep(1); setResult(null);
  };

  const addFiles = (incoming) => {
    const arr = Array.from(incoming);
    if (mode !== 'ai') { if (arr[0]) setFiles([arr[0]]); return; }
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name));
      return [...prev, ...arr.filter(f => !existing.has(f.name))];
    });
  };

  const removeFile = (name) => setFiles(prev => prev.filter(f => f.name !== name));

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const accept = mode === 'ai' ? '.pdf,image/*' : '.xlsx,.xls,.csv';
  const modeInfo = MODES.find(m => m.id === mode);
  const modeColor = modeInfo.color;

  // ── Analyze handlers ──────────────────────────────────────────────────────
  const handleAnalyzeAI = async () => {
    if (files.length === 0) return toast.error('Please select at least one file');
    setAnalyzing(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('menu', f));
      const res = await api.post('/import/analyze', fd);
      setResult(res.data);
      setExpandedCat(res.data.menu.categories[0]?.name || null);
      setStep(2);
      api.get('/import/balance').then(r => setBalance(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.error || 'Analysis failed');
    } finally { setAnalyzing(false); }
  };

  const handleAnalyzeExcel = async () => {
    if (files.length === 0) return toast.error('Please select an Excel file');
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('excel', files[0]);
      const res = await api.post('/import/analyze-excel', fd);
      setResult(res.data);
      setExpandedCat(res.data.menu.categories[0]?.name || null);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to read Excel file');
    } finally { setAnalyzing(false); }
  };

  const handleAnalyzeExcelAI = async () => {
    if (files.length === 0) return toast.error('Please select an Excel file');
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('excel', files[0]);
      const res = await api.post('/import/analyze-excel-ai', fd);
      setResult(res.data);
      setExpandedCat(res.data.menu.categories[0]?.name || null);
      setStep(2);
      api.get('/import/balance').then(r => setBalance(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.error || 'AI failed to read Excel file');
    } finally { setAnalyzing(false); }
  };

  const handleAnalyze = mode === 'ai' ? handleAnalyzeAI : mode === 'excel-ai' ? handleAnalyzeExcelAI : handleAnalyzeExcel;

  const handleCreate = async () => {
    if (!selectedRest) return toast.error('Please select a brand');
    setCreating(true);
    try {
      const res = await api.post('/import/create', {
        brand_id: parseInt(selectedRest),
        menu_data: result.menu,
        append_mode: appendMode
      });
      toast.success(`✅ ${res.data.categories} categories, ${res.data.items} items ${appendMode ? 'added' : 'created'}!`);
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create menu');
    } finally { setCreating(false); }
  };

  const selectedRestName = restaurants.find(r => r.id === parseInt(selectedRest))?.name;
  const totalItems = result?.menu?.categories?.reduce((s, c) => s + (c.items?.length || 0), 0) || 0;
  const lang = result ? detectLang(result.menu) : null;

  // ── Step bar ──────────────────────────────────────────────────────────────
  const StepBar = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 20 }}>
      {['Upload', 'Brand', 'Preview', 'Done'].map((label, i) => {
        const n = i + 1;
        const done = step > n, active = step === n;
        return (
          <React.Fragment key={n}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 11, fontWeight: 700,
                background: step >= n ? modeColor : '#E0E0E0',
                color: step >= n ? '#fff' : '#aaa',
                transition: 'all 0.2s'
              }}>{done ? '✓' : n}</div>
              <span style={{ fontSize: 12, color: active ? modeColor : step > n ? '#666' : '#bbb', fontWeight: active ? 700 : 400 }}>{label}</span>
            </div>
            {n < 4 && <div style={{ flex: 1, height: 2, background: step > n ? modeColor : '#E0E0E0', borderRadius: 1, minWidth: 8 }} />}
          </React.Fragment>
        );
      })}
    </div>
  );

  // ── Drop zone ─────────────────────────────────────────────────────────────
  const DropZone = () => {
    const isExcel = mode !== 'ai';
    const singleFile = files[0];
    const isEmpty = files.length === 0;

    const zoneStyle = {
      border: `2px dashed ${dragging ? modeColor : isEmpty ? '#D0D0D0' : modeColor}`,
      borderRadius: 14, padding: isEmpty ? '32px 20px' : 16,
      textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
      background: dragging ? modeInfo.bg : isEmpty ? '#fafafa' : modeInfo.bg,
      marginBottom: 12
    };

    const handleClick = () => fileInputRef.current?.click();

    // Excel / Smart Excel: single file preview
    if (isExcel && singleFile) {
      return (
        <div style={zoneStyle} onClick={handleClick}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}>
          <div style={{ fontSize: 36, marginBottom: 6 }}>📊</div>
          <div style={{ fontWeight: 600, fontSize: 14, color: modeColor, marginBottom: 2 }}>{singleFile.name}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{(singleFile.size / 1024).toFixed(0)} KB · Tap to change</div>
          <input ref={fileInputRef} type="file" accept={accept} style={{ display: 'none' }}
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
        </div>
      );
    }

    // AI mode with files: show thumbnails
    if (!isExcel && files.length > 0) {
      return (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            border: `2px dashed ${modeColor}`, borderRadius: 14, padding: 12,
            background: modeInfo.bg
          }}>
            {/* Thumbnail grid */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              {files.map((f, i) => (
                <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                  {previews[f.name] ? (
                    <img src={previews[f.name]} alt={f.name}
                      style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, display: 'block', border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.15)' }} />
                  ) : (
                    <div style={{ width: 72, height: 72, borderRadius: 8, background: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>
                      📑
                    </div>
                  )}
                  <button
                    onClick={e => { e.stopPropagation(); removeFile(f.name); }}
                    style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#f44336', color: 'white', border: 'none', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, lineHeight: 1 }}>
                    ✕
                  </button>
                  <div style={{ fontSize: 9, color: '#666', marginTop: 3, maxWidth: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>{f.name}</div>
                </div>
              ))}
              {/* Add more tile */}
              <div onClick={() => fileInputRef.current?.click()}
                style={{ width: 72, height: 72, borderRadius: 8, border: `2px dashed ${modeColor}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: modeColor, fontSize: 22, gap: 2 }}>
                <span>+</span>
                <span style={{ fontSize: 9, fontWeight: 600 }}>Add</span>
              </div>
            </div>
            <div style={{ fontSize: 12, color: modeColor, fontWeight: 600 }}>{files.length} file{files.length > 1 ? 's' : ''} selected</div>
          </div>
          <input ref={fileInputRef} type="file" accept={accept} multiple style={{ display: 'none' }}
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
        </div>
      );
    }

    // Empty state
    return (
      <div style={zoneStyle} onClick={handleClick}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>{modeInfo.icon}</div>
        <div style={{ fontWeight: 600, fontSize: 15, color: modeColor }}>
          {isExcel ? 'Click or drag & drop' : 'Tap to add files'}
        </div>
        <div style={{ fontSize: 13, color: '#aaa', marginTop: 4 }}>
          {mode === 'ai' ? 'PDF or images — can select multiple' : mode === 'excel' ? '.xlsx, .xls, .csv' : 'Any .xlsx, .xls, .csv'}
        </div>
        <input ref={fileInputRef} type="file" accept={accept} multiple={mode === 'ai'} style={{ display: 'none' }}
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
      </div>
    );
  };

  // ── Cost + Balance bar ────────────────────────────────────────────────────
  const CostBar = () => {
    if (mode === 'excel') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 9, padding: '9px 14px', fontSize: 13, color: '#2E7D32', marginBottom: 16 }}>
          <span style={{ fontSize: 16 }}>🆓</span>
          <span><strong>Free</strong> — no AI cost, data read directly from file</span>
        </div>
      );
    }
    const cost = files.length > 1 ? `${files.length} files ≈ $${(files.length * 0.01).toFixed(2)}` : mode === 'excel-ai' ? '~$0.01–0.05' : '~$0.01/file';
    const showBalance = balance && (mode === 'ai' || mode === 'excel-ai');
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 9, padding: '9px 14px', fontSize: 13, color: '#795548', marginBottom: 16 }}>
        <span>💡</span>
        <span style={{ flex: 1 }}>Cost: <strong>{cost}</strong> · Claude {mode === 'ai' ? 'Haiku' : 'Opus'}</span>
        {showBalance && (
          <span style={{
            fontWeight: 700, fontSize: 13, padding: '2px 10px', borderRadius: 20,
            background: balance.remaining < 5 ? '#FFEBEE' : '#E8F5E9',
            color: balance.remaining < 5 ? '#C62828' : '#2E7D32',
            whiteSpace: 'nowrap'
          }}>
            {balance.remaining < 5 ? '⚠️ ' : '💳 '}${parseFloat(balance.remaining).toFixed(2)} left
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="page" style={{ paddingBottom: 48 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 12 }}>
        <div>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: '#2196F3', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 6 }}>← Dashboard</button>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>📥 Menu Import</h1>
        </div>
        {/* Balance chip */}
        <div style={{ flexShrink: 0, marginTop: 22 }}>
          <button
            onClick={() => { setShowBalanceMgr(p => !p); setBalanceInput(''); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20,
              border: `1.5px solid ${balance && balance.remaining < 5 ? '#F44336' : '#4CAF50'}`,
              background: balance && balance.remaining < 5 ? '#FFEBEE' : '#E8F5E9',
              cursor: 'pointer', fontSize: 13, fontWeight: 700,
              color: balance && balance.remaining < 5 ? '#C62828' : '#2E7D32'
            }}>
            <span>{balance && balance.remaining < 5 ? '⚠️' : '💳'}</span>
            <span>{balance ? `$${parseFloat(balance.remaining).toFixed(2)}` : '…'}</span>
            <span style={{ fontSize: 10, fontWeight: 400, color: '#888' }}>⚙</span>
          </button>
        </div>
      </div>

      {/* Balance manager panel */}
      {showBalanceMgr && (
        <div style={{ background: '#fff', border: '1.5px solid #E0E0E0', borderRadius: 14, padding: 16, marginBottom: 20, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: '#333' }}>💳 API Balance Settings</div>

          {/* Current balance info */}
          {balance && (
            <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ background: '#f5f5f5', borderRadius: 9, padding: '8px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#888' }}>Current Balance</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: balance.remaining < 5 ? '#F44336' : '#2E7D32' }}>${parseFloat(balance.remaining).toFixed(2)}</div>
              </div>
              <div style={{ background: '#f5f5f5', borderRadius: 9, padding: '8px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#888' }}>Total Spent</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#555' }}>${parseFloat(balance.total_cost || 0).toFixed(4)}</div>
              </div>
              <div style={{ background: '#f5f5f5', borderRadius: 9, padding: '8px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#888' }}>Imports</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#555' }}>{balance.total_imports || 0}</div>
              </div>
            </div>
          )}

          {/* Check actual balance link */}
          <a
            href="https://platform.claude.com/workspaces/default/cost"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#E3F2FD', borderRadius: 9, textDecoration: 'none', color: '#1565C0', fontSize: 13, fontWeight: 600, marginBottom: 12, border: '1px solid #BBDEFB' }}>
            <span style={{ fontSize: 18 }}>🔗</span>
            <div style={{ flex: 1 }}>
              <div>Check actual balance on Anthropic Console</div>
              <div style={{ fontSize: 11, fontWeight: 400, color: '#1976D2' }}>platform.claude.com → Workspaces → Cost</div>
            </div>
            <span style={{ fontSize: 14 }}>↗</span>
          </a>

          {/* Manual input */}
          <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Enter your actual balance from the Anthropic console:</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#555', fontWeight: 700, fontSize: 15 }}>$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={balanceInput}
                onChange={e => setBalanceInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSaveBalance()}
                placeholder={balance ? parseFloat(balance.remaining).toFixed(2) : '0.00'}
                style={{ width: '100%', padding: '9px 10px 9px 24px', borderRadius: 8, border: '1.5px solid #E0E0E0', fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
              />
            </div>
            <button
              onClick={handleSaveBalance}
              disabled={savingBalance || !balanceInput}
              style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: !balanceInput || savingBalance ? '#E0E0E0' : '#4CAF50', color: !balanceInput || savingBalance ? '#aaa' : '#fff', fontWeight: 700, fontSize: 13, cursor: !balanceInput || savingBalance ? 'not-allowed' : 'pointer' }}>
              {savingBalance ? '…' : 'Save'}
            </button>
            <button
              onClick={() => setShowBalanceMgr(false)}
              style={{ padding: '9px 14px', borderRadius: 8, border: '1.5px solid #E0E0E0', background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Mode tabs — only on step 1 */}
      {step === 1 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {MODES.map(m => (
            <button key={m.id} onClick={() => switchMode(m.id)}
              style={{
                flex: 1, padding: '10px 6px', borderRadius: 12, cursor: 'pointer', border: `2px solid ${mode === m.id ? m.color : '#E0E0E0'}`,
                background: mode === m.id ? m.bg : '#fff', transition: 'all 0.15s',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2
              }}>
              <span style={{ fontSize: 20 }}>{m.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: mode === m.id ? m.light : '#555' }}>{m.label}</span>
              <span style={{ fontSize: 10, color: mode === m.id ? m.color : '#bbb', fontWeight: 400 }}>{m.badge}</span>
            </button>
          ))}
        </div>
      )}

      {StepBar()}

      {/* ══ STEP 1 ══════════════════════════════════════════════════════════ */}
      {step === 1 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
                {mode === 'ai' ? 'Upload Menu Files' : mode === 'excel' ? 'Upload Excel Sheet' : 'Smart Excel Import'}
              </h2>
              <p style={{ fontSize: 13, color: '#888', margin: '3px 0 0' }}>
                {mode === 'ai' ? 'Supports PDF and images — select multiple to merge' : mode === 'excel' ? 'Use our column format for instant, free import' : 'Upload any Excel — AI reads the layout for you'}
              </p>
            </div>
            {mode === 'excel' && <span style={{ fontSize: 11, background: '#C8E6C9', color: '#1B5E20', padding: '3px 10px', borderRadius: 20, fontWeight: 700, flexShrink: 0 }}>FREE</span>}
          </div>

          {DropZone()}

          {/* Excel column guide */}
          {mode === 'excel' && (
            <div style={{ background: '#F3F8FF', border: '1px solid #BBDEFB', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
              <div onClick={() => setShowCols(p => !p)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', cursor: 'pointer' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#1565C0' }}>📋 Required column format</span>
                <span style={{ fontSize: 11, color: '#1976D2' }}>{showCols ? '▲ Hide' : '▼ Show'}</span>
              </div>
              {showCols && (
                <div style={{ padding: '0 14px 12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {EXCEL_COLS.map((c, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12 }}>
                        <code style={{ background: '#E3F2FD', color: '#0D47A1', padding: '1px 7px', borderRadius: 4, fontFamily: 'monospace', whiteSpace: 'nowrap', flexShrink: 0 }}>{c.col}</code>
                        <span style={{ color: '#555' }}>{c.note}</span>
                        {c.required && <span style={{ color: '#F44336', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>req.</span>}
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 11, color: '#757575', background: '#fff', borderRadius: 6, padding: '7px 10px', border: '1px solid #E0E0E0' }}>
                    💡 Arabic column headers supported · Arabic-only files auto-detected
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Smart Excel info */}
          {mode === 'excel-ai' && (
            <div style={{ background: '#F3E5F5', border: '1px solid #CE93D8', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: '#4A148C', marginBottom: 4 }}>🧠 What AI does:</div>
              <div style={{ color: '#555', display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span>✔ Understands any column layout</span>
                <span>✔ Detects Arabic, English or mixed files</span>
                <span>✔ Works with POS exports and custom price lists</span>
              </div>
            </div>
          )}

          {CostBar()}

          <button
            onClick={handleAnalyze}
            disabled={files.length === 0 || analyzing}
            style={{
              width: '100%', padding: 14, borderRadius: 10, border: 'none', cursor: files.length === 0 || analyzing ? 'not-allowed' : 'pointer',
              background: files.length === 0 || analyzing ? '#E0E0E0' : modeColor,
              color: files.length === 0 || analyzing ? '#aaa' : '#fff',
              fontWeight: 700, fontSize: 15, transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10
            }}>
            {analyzing ? (
              <>
                <span style={{ width: 17, height: 17, border: '2px solid rgba(255,255,255,0.35)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                {mode === 'excel' ? 'Reading file…' : `Analyzing ${files.length} file${files.length > 1 ? 's' : ''}…`}
              </>
            ) : (
              `${modeInfo.icon} ${mode === 'excel' ? 'Import from Excel' : mode === 'excel-ai' ? 'Smart Import with AI' : `Analyze ${files.length > 0 ? files.length + ' File' + (files.length > 1 ? 's' : '') : 'Menu'}`}`
            )}
          </button>
        </div>
      )}

      {/* ══ STEP 2: BRAND ═══════════════════════════════════════════════════ */}
      {step === 2 && result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Summary */}
          <div className="card" style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', padding: '14px 16px' }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
              ✅ {mode === 'excel' ? 'Excel Imported!' : 'Analysis Complete!'}
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13 }}>
              <span>📂 <strong>{result.summary.categories}</strong> categories</span>
              <span>🍽 <strong>{result.summary.items}</strong> items</span>
              {result.usage?.files_processed > 1 && <span>📄 <strong>{result.usage.files_processed}</strong> files merged</span>}
              {result.usage && <span>💰 <strong style={{ color: mode === 'excel' ? '#2E7D32' : undefined }}>{result.usage.cost_display}</strong></span>}
              {result.usage?.balance_display && result.usage.balance_display !== '—' && (
                <span>💳 <strong style={{ color: result.usage.low_balance ? '#F44336' : '#2E7D32' }}>{result.usage.balance_display} left</strong></span>
              )}
            </div>
            {/* Language detection note for Excel */}
            {mode === 'excel' && lang && lang !== 'bilingual' && (
              <div style={{ marginTop: 8, fontSize: 12, padding: '6px 10px', background: 'rgba(255,255,255,0.6)', borderRadius: 7, color: '#555' }}>
                {lang === 'arabic'
                  ? '🇸🇦 Arabic file detected — items saved to Arabic field. Use Menu Translation to add English names.'
                  : '🇬🇧 English file detected — items saved to English field. Use Menu Translation to add Arabic names.'}
              </div>
            )}
            {result.usage?.low_balance && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#F44336', fontWeight: 600 }}>⚠️ Balance below $5 — top up at console.anthropic.com</div>
            )}
          </div>

          {/* Brand selector */}
          <div className="card">
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>Select Target Brand</h2>
            <div className="input-group">
              <label>Brand</label>
              <select className="input" value={selectedRest} onChange={e => setSelectedRest(e.target.value)}>
                <option value="">— Select Brand —</option>
                {restaurants.map(r => <option key={r.id} value={r.id}>{r.name}{r.name_ar ? ' · ' + r.name_ar : ''}</option>)}
              </select>
            </div>

            {selectedRest && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <button type="button" onClick={() => setAppendMode(false)}
                    style={{ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${!appendMode ? '#F44336' : '#E0E0E0'}`, background: !appendMode ? '#FFEBEE' : '#fff', color: !appendMode ? '#C62828' : '#555', fontWeight: !appendMode ? 700 : 400, cursor: 'pointer', fontSize: 13 }}>
                    🔄 Replace Menu
                  </button>
                  <button type="button" onClick={() => setAppendMode(true)}
                    style={{ flex: 1, padding: '10px', borderRadius: 8, border: `2px solid ${appendMode ? '#4CAF50' : '#E0E0E0'}`, background: appendMode ? '#E8F5E9' : '#fff', color: appendMode ? '#2E7D32' : '#555', fontWeight: appendMode ? 700 : 400, cursor: 'pointer', fontSize: 13 }}>
                    ➕ Add to Menu
                  </button>
                </div>
                <div style={{ fontSize: 12, color: '#757575', padding: '7px 11px', background: '#f5f5f5', borderRadius: 7 }}>
                  {appendMode ? '✅ New items will be added to existing menu' : '⚠️ Existing menu will be completely replaced'}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setStep(3)}>👁 Preview</button>
            <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleCreate} disabled={!selectedRest || creating}>
              {creating ? 'Creating…' : appendMode ? '➕ Add to Menu' : '✅ Create Menu'}
            </button>
          </div>
        </div>
      )}

      {/* ══ STEP 3: PREVIEW ═════════════════════════════════════════════════ */}
      {step === 3 && result && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Menu Preview</h2>
              <p style={{ fontSize: 12, color: '#888', margin: '2px 0 0' }}>{result.summary.categories} categories · {totalItems} items</p>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setStep(2)}>← Back</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {result.menu.categories.map((cat, ci) => (
              <div key={ci} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div onClick={() => setExpandedCat(expandedCat === cat.name ? null : cat.name)}
                  style={{ padding: '11px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: expandedCat === cat.name ? '#E3F2FD' : '#f9f9f9', borderBottom: expandedCat === cat.name ? '1px solid #E0E0E0' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>{cat.icon || '🍽'}</span>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 14, color: '#2196F3' }}>{cat.name || cat.name_ar}</span>
                      {cat.name && cat.name_ar && <span style={{ fontSize: 12, color: '#999', marginLeft: 6, fontFamily: 'Cairo' }}>{cat.name_ar}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, background: '#2196F3', color: 'white', borderRadius: 10, padding: '1px 7px' }}>{cat.items?.length || 0}</span>
                    <span style={{ fontSize: 12, color: '#999' }}>{expandedCat === cat.name ? '▲' : '▼'}</span>
                  </div>
                </div>

                {expandedCat === cat.name && (
                  <div>
                    {(cat.items || []).map((item, ii) => (
                      <div key={ii} style={{ padding: '10px 14px', borderBottom: ii < cat.items.length - 1 ? '1px solid #f5f5f5' : 'none', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <span style={{ fontSize: 20, flexShrink: 0 }}>{item.icon || '🍽'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{item.name || item.name_ar}</div>
                          {item.name && item.name_ar && <div style={{ fontSize: 11, color: '#888', fontFamily: 'Cairo' }}>{item.name_ar}</div>}
                          {item.description && <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>{item.description}</div>}
                        </div>
                        <div style={{ flexShrink: 0, textAlign: 'right' }}>
                          {item.variants?.filter(v => v.price > 0).length > 0 ? (
                            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {item.variants.filter(v => v.price > 0).map((v, vi) => (
                                <span key={vi} style={{ fontSize: 11, background: '#E3F2FD', color: '#1565C0', padding: '1px 6px', borderRadius: 8, whiteSpace: 'nowrap' }}>
                                  {v.name || v.name_ar}: {v.price}
                                </span>
                              ))}
                            </div>
                          ) : item.price > 0 ? (
                            <span style={{ fontWeight: 700, color: '#2196F3', fontSize: 13 }}>{item.price}</span>
                          ) : (
                            <span style={{ fontSize: 11, color: '#ddd' }}>—</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setStep(2)}>← Back</button>
            <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', fontSize: 14, padding: 13 }} onClick={handleCreate} disabled={!selectedRest || creating}>
              {creating ? 'Creating…' : selectedRest ? (appendMode ? `➕ Add to ${selectedRestName}` : `✅ Import to ${selectedRestName}`) : 'Select a brand first'}
            </button>
          </div>
        </div>
      )}

      {/* ══ STEP 4: DONE ════════════════════════════════════════════════════ */}
      {step === 4 && (
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>{appendMode ? 'Items Added!' : 'Menu Created!'}</h2>
          <p style={{ fontSize: 14, color: '#888', marginBottom: 14 }}>
            {appendMode ? 'New items have been added to the existing menu.' : 'All categories and items imported successfully.'}
          </p>
          {result && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, fontSize: 13, marginBottom: 24 }}>
              <span>📂 <strong>{result.summary.categories}</strong> categories</span>
              <span>🍽 <strong>{result.summary.items}</strong> items</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={() => selectedRest && navigate(`/brands/${selectedRest}/menu`)}>View Menu Editor</button>
            <button className="btn btn-outline" onClick={() => { setStep(1); setFiles([]); setResult(null); setSelectedRest(''); setAppendMode(false); }}>Import Another</button>
          </div>
        </div>
      )}
    </div>
  );
}
